import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import InfiniteScroll from "react-infinite-scroll-component";

import { getTrilhaCliente } from "~services/GlobalServices";
import { Spinner } from "@chakra-ui/react";

import "./style.scss";
import LoaderFull from "~components/Form/components/LoaderFull";
import PriceSearchItem from "~pages/PriceSearchV2/components/PriceSearchItem";
import ModalAction from "~components/Modal/ModalAction";
import ModalResponse from "~components/Modal/ModalResponse";

const PriceSearchCards = ({
  pesquisaObjetos,
  pesquisas,
  toDeleteModalShow,
  dados,
  loading,
  lastSearchDate,
  filters,
  updateHook,
  diasMostrarDataAtualizacao,
  buttonClickClear,
}) => {
  const [trilhaCliente, setTrilhaCliente] = useState("");
  const [visibleItems, setVisibleItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [prevPesquisasLength, setPrevPesquisasLength] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [modalSettings, setModalSettings] = useState({
    isOpen: false,
    field: "",
  });

  const [modalResponseSettings, setModalResponseSettings] = useState({
    closeCallback: () => {},
    isOpen: false,
    message: "",
  });

  const removeDuplicateItems = (items) => {
    return items.filter((item, index, self) => index === self.findIndex((t) => t.id === item.id));
  };

  useEffect(() => {
    if (dados?.trilha) setTrilhaCliente(getTrilhaCliente(dados?.trilha?.codigo));
  }, [dados]);

  const activePesquisas = useMemo(() => pesquisas.filter((pesquisa) => pesquisa.ativo === true), [pesquisas]);

  useEffect(() => {
    if (prevPesquisasLength !== activePesquisas.length || filters) {
      const initialItems = activePesquisas.slice(0, 15);
      setVisibleItems(initialItems);
      setHasMore(initialItems.length < activePesquisas.length);
      setPrevPesquisasLength(activePesquisas.length);
    }
  }, [activePesquisas, prevPesquisasLength, filters]);

  const fetchMoreData = () => {
    if (visibleItems.length >= activePesquisas.length || loadingMore) {
      setHasMore(false);
      return;
    }

    setLoadingMore(true);

    const nextItems = activePesquisas.slice(visibleItems.length, visibleItems.length + 15);
    const combinedItems = [...visibleItems, ...nextItems];
    const uniqueItems = removeDuplicateItems(combinedItems);

    setVisibleItems(uniqueItems);
    setHasMore(visibleItems.length + nextItems.length < activePesquisas.length);
    setLoadingMore(false);
  };

  return (
    <>
      <div className="bg-white p-4 rounded-lg mt-4">
        {loading ? (
          <div className="flex items-center justify-center w-full p-8">
            <LoaderFull />
          </div>
        ) : (
          <InfiniteScroll
            dataLength={visibleItems.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={
              <div className="flex items-center justify-center p-4 min-h-[100px]">
                <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="brand.main" size="lg" />
              </div>
            }
            endMessage={<p className="text-center text-gray-500 mt-4">Todos os itens foram carregados!</p>}
          >
            {visibleItems.map((pesquisa) => (
              <PriceSearchItem
                pesquisaObjetos={pesquisaObjetos}
                key={pesquisa.id}
                pesquisa={pesquisa}
                handleDelete={toDeleteModalShow}
                trilhaCliente={trilhaCliente}
                setModalSettings={setModalSettings}
                setModalResponseSettings={setModalResponseSettings}
                lastSearchDate={lastSearchDate}
                updateHook={updateHook}
                diasMostrarDataAtualizacao={diasMostrarDataAtualizacao}
                buttonClickClear={buttonClickClear}
              />
            ))}
          </InfiniteScroll>
        )}
      </div>

      <ModalAction
        title={"Confirma a ação?"}
        sub={modalSettings.sub ?? `Ao preencher o ${modalSettings.field} com 0, a pesquisa será atualizada para "Sem Informação". Deseja confirmar?`}
        isOpen={modalSettings.isOpen}
        buttonOne={"Sim"}
        linkOne={() => {
          if (modalSettings.handleConfirm && typeof modalSettings.handleConfirm === "function") {
            const updated = modalSettings.handleConfirm();

            if (!updated) {
              if (modalSettings.handleCancel && typeof modalSettings.handleCancel === "function") {
                modalSettings.handleCancel();
              }
            }
          }

          setModalSettings({ isOpen: false });
        }}
        buttonTwo={"Não"}
        linkTwo={() => {
          if (modalSettings.handleCancel && typeof modalSettings.handleCancel === "function")
            modalSettings.handleCancel();

          setModalSettings({ isOpen: false });
        }}
        onClose={() => {
          if (modalSettings.handleCancel && typeof modalSettings.handleCancel === "function")
            modalSettings.handleCancel();

          setModalSettings({ isOpen: false });
        }}
      />

      <ModalResponse
        status={"warning"}
        isOpen={modalResponseSettings.isOpen}
        message={<p className="max-w-[400px]">{modalResponseSettings.message}</p>}
        closeModal={() => {
          if (modalResponseSettings.closeCallback && typeof modalResponseSettings.closeCallback === "function")
            modalResponseSettings.closeCallback();

          setModalResponseSettings({ isOpen: false });
        }}
      />
    </>
  );
};

PriceSearchCards.propTypes = {
  pesquisaObjetos: PropTypes.shape({
    pesquisaCanaisDistribuicao: PropTypes.object,
    pesquisaGrupoMateriais: PropTypes.object,
    pesquisaProdutos: PropTypes.object,
    pesquisaEmbalagens: PropTypes.object,
    pesquisaPlayers: PropTypes.object,
    pesquisaPrazos: PropTypes.object,
    pesquisaMedidas: PropTypes.object,
    pesquisaCentros: PropTypes.object,
  }),
  pesquisas: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      ativo: PropTypes.bool.isRequired,
      clienteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      ultimaAtualizacao: PropTypes.string,
      ultimaAtualizacaoEN: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      updatedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      createdAtEN: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      updatedAtEN: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      clienteFechado: PropTypes.bool,
      clienteNaoConsomeBasicos: PropTypes.bool,
      dadosGerais: PropTypes.shape({
        incoterms: PropTypes.number,
        produtoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        grupoProdutoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        grupoProduto: PropTypes.shape({
          nome: PropTypes.string,
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        produto: PropTypes.shape({
          nome: PropTypes.string,
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        embalagemCapacidade: PropTypes.shape({
          nome: PropTypes.string,
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        canal: PropTypes.shape({
          nome: PropTypes.string,
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        pesquisaCentroOrigem: PropTypes.shape({
          nome: PropTypes.string,
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        player: PropTypes.shape({
          nome: PropTypes.string,
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
      }),
      infosAdicionais: PropTypes.shape({
        volume: PropTypes.number,
        sellIn: PropTypes.number,
        sellOut: PropTypes.number,
        precoFOB: PropTypes.number,
        precoCIF: PropTypes.number,
        medida: PropTypes.shape({
          nome: PropTypes.string,
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
      }),
    })
  ).isRequired,
  toDeleteModalShow: PropTypes.func.isRequired,
  dados: PropTypes.shape({
    trilha: PropTypes.shape({
      codigo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  loading: PropTypes.bool.isRequired,
  lastSearchDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  filters: PropTypes.shape({
    produto: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    grupoProduto: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    periodo: PropTypes.oneOf(["hoje", "seteDias", "quinzeDias", "trintaDias"]),
    order: PropTypes.oneOf(["recentes", "asc"]),
  }),
  updateHook: PropTypes.func.isRequired,
  diasMostrarDataAtualizacao: PropTypes.number.isRequired,
  buttonClickClear: PropTypes.func,
};

PriceSearchCards.defaultProps = {
  pesquisaObjetos: {},
  dados: null,
  lastSearchDate: null,
  filters: null,
  buttonClickClear: null,
};

export default PriceSearchCards;
